import { globalAppConfig } from "../../../config";
import { GlobalStoreContextType } from "../../../context/GlobalStoreContext";
import { MapObject } from "../../../models/MapObject";
import { PdfMapCapturePadding } from "../../PDF/Shared/PdfMapCapture";
import { PdfMapCrop } from "../../PDF/Shared/PdfMapFrameEditor";
import { PdfMapLogo } from "../../PDF/Shared/PdfMapView";
import { DamnMapItems } from "../Map/useDamnMapItems";
import type { DamnPdfInclude } from "./damnPdfOptions";

// The manifest's two maps: the approach, framed to the haul route so the roads in read clearly, and the jobsite, framed
// tight on the gate, dropoff and the route between them. With both shown they sit side by side; with one, it's full
// width.
export type DamnPdfMapKind = "approach" | "site";
export type DamnPdfMapLayout = "single" | "pair";

export const DAMN_PDF_MAP_KINDS: DamnPdfMapKind[] = ["approach", "site"];

// `title` and `detail` head the map in the PDF; the rest name it in the export dialog.
export const DAMN_PDF_MAP_LABELS: Record<
    DamnPdfMapKind,
    { title: string; detail: string; option: string; adjust: string }
> = {
    approach: {
        title: "Haul Route",
        detail: "Pre-approved approach to the site",
        option: "Haul Route Map",
        adjust: "Adjust haul route map",
    },
    site: { title: "Site Route", detail: "Gate to Dropoff", option: "Jobsite map", adjust: "Adjust jobsite map" },
};

// Streets for the approach, where the roads matter; the project's own style on site, like the delivery's map in the
// calendar, so its imagery shows.
export const damnPdfMapStyle = (kind: DamnPdfMapKind, project: any) =>
    kind === "approach"
        ? "mapbox://styles/mapbox/streets-v12"
        : project?.map_style || globalAppConfig.ARC_GIS_IMAGERY_URL;

// Each map is printed at its tallest aspect and, to fit one page, cropped as short as its shortest (see
// DAMN_PDF_DENSITIES). It's captured at the tallest with the items framed inside the shortest's centre band.
export const DAMN_PDF_MAP_ASPECTS: Record<DamnPdfMapLayout, { tallest: number; shortest: number }> = {
    single: { tallest: 1.8, shortest: 2.8 },
    pair: { tallest: 1.2, shortest: 1.8 },
};

interface DamnPdfMapFrame {
    width: number;
    height: number;
    padding: PdfMapCapturePadding;
    // The bands the PDF may crop off, shaded in the framing editor.
    crop: PdfMapCrop;
}

const EDGE_PADDING = 50;

const mapFrame = (width: number, { tallest, shortest }: { tallest: number; shortest: number }): DamnPdfMapFrame => {
    const height = Math.round(width / tallest);
    const inset = (height - width / shortest) / 2;
    return {
        width,
        height,
        padding: {
            top: inset + EDGE_PADDING,
            bottom: inset + EDGE_PADDING,
            left: EDGE_PADDING,
            right: EDGE_PADDING,
        },
        crop: { top: inset / height, bottom: inset / height, left: 0, right: 0 },
    };
};

export const DAMN_PDF_MAP_FRAMES: Record<DamnPdfMapLayout, DamnPdfMapFrame> = {
    single: mapFrame(1200, DAMN_PDF_MAP_ASPECTS.single),
    pair: mapFrame(900, DAMN_PDF_MAP_ASPECTS.pair),
};

// Framed views are kept with their zoom for a map this wide, so one still shows the same area when the layout (and so
// the capture's width) changes.
export const DAMN_PDF_CAMERA_WIDTH = DAMN_PDF_MAP_FRAMES.single.width;

export interface DamnPdfMapContent {
    // Everything drawn, in drawing order (points last, so they sit on top of the lines).
    draw: MapObject[];
    // What the automatic frame fits. Empty when the map has nothing of its own to show.
    fit: MapObject[];
}

const present = (mapObjects: (MapObject | null | undefined)[]) =>
    mapObjects.filter((mapObject): mapObject is MapObject => !!mapObject);

// The assigned haul route when there is one (the others are left off, as in the rest of the PDF), otherwise the
// approved routes to choose from. With `routesShown` off (see damnPdfRoutesShown) they're left off, which leaves the
// approach map with nothing to show.
export const damnPdfMapContents = (
    mapItems: Pick<
        DamnMapItems,
        | "selectedHaulRoute"
        | "selectedHaulRouteMapObject"
        | "haulRoutes"
        | "gateToDropoffRoutes"
        | "gateMapObject"
        | "dropOffMapObject"
    >,
    context: GlobalStoreContextType,
    routesShown: boolean
): Record<DamnPdfMapKind, DamnPdfMapContent> => {
    const haulRoutes = routesShown
        ? present(
              mapItems.selectedHaulRoute
                  ? [mapItems.selectedHaulRouteMapObject]
                  : mapItems.haulRoutes.map((route) => route.getMapObject(context))
          )
        : [];
    const gateToDropoffRoutes = present(mapItems.gateToDropoffRoutes.map((route) => route.getMapObject(context)));
    const places = present([mapItems.gateMapObject, mapItems.dropOffMapObject]);

    // The route in, and the gate it ends at when the delivery has one. The jobsite itself is marked by the receiving
    // team's logo (see damnPdfJobsiteLogo); the dropoff is left to the jobsite map.
    const approachPlaces = present([mapItems.gateMapObject]);

    return {
        approach:
            haulRoutes.length > 0
                ? { draw: [...haulRoutes, ...approachPlaces], fit: [...haulRoutes, ...approachPlaces] }
                : { draw: [], fit: [] },
        // Just the jobsite: the haul route belongs to the approach map.
        site: { draw: [...gateToDropoffRoutes, ...places], fit: [...gateToDropoffRoutes, ...places] },
    };
};

// The receiving team's logo on the project's jobsite coordinates, for the haul route map. Null without a logo or
// coordinates.
export const damnPdfJobsiteLogo = (project: any, teamLogo: string | null | undefined): PdfMapLogo | null => {
    const longitudeValue = project?.lon_override ?? project?.lon;
    const latitudeValue = project?.lat_override ?? project?.lat;
    const longitude = Number(longitudeValue);
    const latitude = Number(latitudeValue);
    if (!teamLogo || longitudeValue == null || latitudeValue == null || !Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
    return { coordinates: [longitude, latitude], image: teamLogo };
};

const INCLUDE_KEYS: Record<DamnPdfMapKind, keyof DamnPdfInclude> = { approach: "approachMap", site: "siteMap" };

// Whether the haul routes are on the maps: the assigned route follows "Haul route", the approved ones "Approved haul
// routes", as in the route list.
export const damnPdfRoutesShown = (include: DamnPdfInclude, routeAssigned: boolean) =>
    routeAssigned ? include.haulRoute : include.otherRoutes;

// Which maps the PDF shows: those with something to show that the person exporting left ticked. `available` is whether
// there's anything for each map at all (see damnPdfMapContents with routes shown); the approach map also needs its
// routes ticked.
export const damnPdfShownMaps = (
    available: Record<DamnPdfMapKind, boolean>,
    include: DamnPdfInclude,
    routeAssigned: boolean
) =>
    DAMN_PDF_MAP_KINDS.filter(
        (kind) =>
            available[kind] &&
            include[INCLUDE_KEYS[kind]] &&
            (kind !== "approach" || damnPdfRoutesShown(include, routeAssigned))
    );

export const damnPdfMapLayout = (shown: DamnPdfMapKind[]): DamnPdfMapLayout => (shown.length === 2 ? "pair" : "single");
